import { DEFAULT_ALGO_WEIGHT } from '@/utils/constants';
import type {
  TerminalAllocation,
  AllocGroupIndex,
  AllocPerPhase,
  AlgorithmWeightResponse,
} from '../models/SolshareSystemSpec';
import type { TConnectionGroup } from '../models/Connection';
import { GROUP_INDEXES, PHASE, PHASE_INDEXES } from './constants';
import type { OutputConnections, Terminal } from '../models/CommissioningDevice';
import { roundTo } from '../../../utils/round';

/**
 * Generate the initial allocations to use as form data
 * The allocation values can be in percentage or kWp format
 *
 * @param connections The connections data saved to the backend.
 * @param rawDetectedConnections The detected connections from the device.
 * @param newAllocationUnit The new allocation unit.
 * @param kwpPerPhase The kwp allocation per phase.
 */
export const generateInitAllocations = (
  connections: TConnectionGroup[],
  rawDetectedConnections: OutputConnections,
  newAllocationUnit: ALLOCATION_UNIT | undefined,
  kwpPerPhase: number,
) => {
  if (!connections || connections.length === 0) {
    throw new Error('Connections data is empty');
  }

  const allocationValues = PHASE_INDEXES.reduce((acc, phaseIndex) => {
    const phaseName = `L${phaseIndex + 1}` as PHASE;
    const connectionNamesForPhase = getConnectionNamesForPhase(connections, phaseName);
    const detectedConnectionsForPhase = getDetectedConnectionsForPhase(
      rawDetectedConnections,
      phaseName,
    );

    return {
      ...acc,
      [phaseIndex]: generateAllocationsPerPhase(
        connectionNamesForPhase,
        detectedConnectionsForPhase,
      ),
    };
  }, {} as TerminalAllocation);


  // The initial allocation unit is express in percentage format
  const currentAllocationUnit = ALLOCATION_UNIT.percentage;

  const convertedAllocations = convertAllocationValues(
    allocationValues,
    currentAllocationUnit, // fromUnit
    newAllocationUnit, // toUnit
    kwpPerPhase,
  );

  return convertedAllocations;
};

/**
 * Return the connection name for a phase. (eg: All 5 connection names for L1)
 *
 * Eg result:
 * { 0: 'Apt101', 1: 'Apt201', 2: 'Apt301', 3: 'No Connection', 4: 'Apt501' }
 */
const getConnectionNamesForPhase = (connections: TConnectionGroup[], phaseName: PHASE) => {
  const collectedConnections = connections.reduce((acc, connection, index) => {
    return {
      ...acc,
      [index]: connection[phaseName],
    };
  }, {} as Record<AllocGroupIndex, string>);

  return collectedConnections;
};

/**
 * Return the detected connections for a phase (eg: All 5 reported connections for L1)
 *
 * Eg result:
 * { 0: { ready: true, state: '' }, 1: { ready: true, state: '' }, 2: { ready: true, state: '' }, 3: { ready: false, state: '' }, 4: { ready: true, state: '' }}
 */
const getDetectedConnectionsForPhase = (
  rawDetectedConnections: OutputConnections,
  phaseName: PHASE,
) => {
  return GROUP_INDEXES.reduce((acc, groupIndex) => {
    const groupNumber = (groupIndex as number) + 1;
    return {
      ...acc,
      [groupIndex]: rawDetectedConnections[`${phaseName}-${groupNumber}` as Terminal],
    };
  }, {} as Record<AllocGroupIndex, { ready: boolean; state: string }>);
};

/**
 * Generate the allocations for a phase.
 *
 * @param phaseConnectionNames - The connection names for a phase.
 * Example: { 0: 'Apt101', 1: 'Apt201', 2: 'Apt301', 3: 'No Connection', 4: 'Apt501' }
 *
 * @returns The allocations for a phase.
 * Example: { 0: 33.33, 1: 0, 2: 33.33, 3: 33.34, 4: 0 }
 */
const generateAllocationsPerPhase = (
  phaseConnectionNames: Record<AllocGroupIndex, string>,
  detectedConnectionsForPhase: Record<AllocGroupIndex, { ready: boolean; state: string }>,
): AllocPerPhase => {
  const connectedGroupIndexes = GROUP_INDEXES.filter((baseGroupIndex) => {
    return detectedConnectionsForPhase[baseGroupIndex].ready;
  });
  const evenValue = Math.floor(10000 / connectedGroupIndexes.length) / 100;

  const prefillEvenly = GROUP_INDEXES.reduce((acc, groupIndex) => {
    const isConnected = detectedConnectionsForPhase[groupIndex].ready;
    return {
      ...acc,
      [groupIndex]: {
        allocationValue: isConnected ? evenValue : 0,
        connectionName: phaseConnectionNames[groupIndex],
        connected: isConnected,
        enable: isConnected,
      },
    };
  }, {} as AllocPerPhase);

  return fillTo100(prefillEvenly, connectedGroupIndexes);
};

const fillTo100 = (prefillEvenly: AllocPerPhase, connectedGroupIndexes: number[]) => {
  if (connectedGroupIndexes.length === 0) {
    return prefillEvenly;
  }

  const sum = Object.values(prefillEvenly).reduce(
    (acc, terminalAlloc) => (acc as number) + (terminalAlloc.allocationValue as number),
    0,
  );
  const remaining = 100 - (sum as number);

  if (remaining === 0) {
    return prefillEvenly;
  } else {
    // Give the remaining value to the last connected terminal in the group
    const lastLiveIndex = connectedGroupIndexes[
      connectedGroupIndexes.length - 1
    ] as AllocGroupIndex;
    return {
      ...prefillEvenly,
      [lastLiveIndex]: {
        ...prefillEvenly[lastLiveIndex],
        allocationValue:
          ((prefillEvenly[lastLiveIndex]?.allocationValue ?? 0) as number) + remaining,
      },
    };
  }
};

/**
 * The precision to round the allocations to.
 * Used for:
 * - Sum of allocations
 * - Allocation values
 * - Kwp allocation per phase
 * - Percentage allocation per phase
 */
const ROUND_PRECISION = 2;

/**
 * Sum the allocations for each phase.
 *
 * @param allocations - The allocations to sum.
 * @returns An array of the sum of the allocations for each phase. eg: [ 100, 100, 100 ]
 */
export const sumAllocations = (allocations: TerminalAllocation) => {
  return Object.values(allocations).map((allocPerPhase) => {
    const sum = Object.values(allocPerPhase).reduce((acc, group) => acc + group.allocationValue, 0);
    return roundTo(sum, ROUND_PRECISION);
  });
};

/**
 * Convert the frontend allocation data to the algorithm weight array expected by the backend API.
 *
 * @param allocations - The frontend allocation data
 */
export const allocationsToAlgorithmWeightArray = (
  allocations: TerminalAllocation,
): AlgorithmWeightResponse => {
  if (!allocations || Object.keys(allocations).length === 0) {
    return DEFAULT_ALGO_WEIGHT as AlgorithmWeightResponse;
  }

  const result = Object.entries(allocations).map(([phaseIndex, allocPerPhase]) => {
    return Object.entries(allocPerPhase).map(([groupIndex, terminalAllocData]) => {
      return [Number(phaseIndex), Number(groupIndex), terminalAllocData.allocationValue];
    });
  });

  return result as AlgorithmWeightResponse;
};

export enum ALLOCATION_UNIT {
  kWp = 'kWp',
  percentage = 'percentage',
}

export const convertAllocationValues = (
  allocations: TerminalAllocation,
  fromUnit: ALLOCATION_UNIT | undefined,
  toUnit: ALLOCATION_UNIT | undefined,
  kwpPerPhase: number,
) => {
  if (fromUnit === undefined || toUnit === undefined) {
    throw new Error('From unit or to unit is undefined');
  }

  if (fromUnit === toUnit) {
    return allocations;
  }

  const __process =
    toUnit === ALLOCATION_UNIT.kWp
      ? // kWp = X * kwpPerPhase / 100
        (value: number) => (value * kwpPerPhase) / 100
      : // percentage = X * 100 / kwpPerPhase
        (value: number) => (kwpPerPhase ? (value * 100) / kwpPerPhase : 0);

  const result = Object.entries(allocations).reduce((phaseAcc, [phaseIndex, allocPerPhase]) => {
    return {
      ...phaseAcc,
      [phaseIndex]: Object.entries(allocPerPhase).reduce((groupAcc, [groupIndex, group]) => {
        return {
          ...groupAcc,
          [groupIndex]: {
            ...group,
            allocationValue: roundTo(__process(group.allocationValue), ROUND_PRECISION),
          },
        };
      }, {} as AllocPerPhase),
    };
  }, {} as TerminalAllocation);

  // Adjust last enabled group to ensure sum equals target (100% or kwp)
  const target = toUnit === ALLOCATION_UNIT.percentage ? 100 : kwpPerPhase;

  Object.entries(result).forEach(([_, allocPerPhase]) => {
    const lastEnabledKey = Object.entries(allocPerPhase)
      .filter(([_, group]) => group.enable)
      .pop()?.[0];

    const sum = Object.values(allocPerPhase).reduce((acc, group) => acc + group.allocationValue, 0);

    if (lastEnabledKey !== undefined) {
      const lastEnabledGroup = allocPerPhase[lastEnabledKey as unknown as AllocGroupIndex];
      lastEnabledGroup.allocationValue = roundTo(
        lastEnabledGroup.allocationValue + target - sum,
        ROUND_PRECISION,
      );
    }
  });

  return result;
};

export const getKwpAllocationPerPhase = (serialNumber: string, baseKwp: number) => {
  if (serialNumber.startsWith('2P')) {
    return roundTo(baseKwp / 2, ROUND_PRECISION);
  } else {
    return roundTo(baseKwp / 3, ROUND_PRECISION);
  }
};
